import type {
  CreateProductBody,
  LineInput,
  Paged,
  Product,
  ProductLine,
  ProductListQuery,
  ProductWithLines,
  TreeNode,
  Unit4Product,
  UpdateProductBody,
} from '@klingerator/shared';
import { api } from './api';

export type ProductListParams = Partial<ProductListQuery>;

export interface LinkLineResult {
  line: ProductLine;
  product: Unit4Product;
  substituted: boolean;
}

export interface MakeCompositeResult {
  line: ProductLine;
  child_product_id: number;
}

/** Sub-producten die met een inzending mee zijn gegaan (of bewust niet). */
export interface CoReviewedProduct {
  id: number;
  name: string;
}
export interface SubmitReviewResult {
  product: Product;
  co_submitted: CoReviewedProduct[];
  skipped_rejected: CoReviewedProduct[];
}
export interface WithdrawReviewResult {
  product: Product;
  co_withdrawn: CoReviewedProduct[];
}

/** Typed wrappers rond de product-/regel-endpoints van de backend. */
export const productService = {
  // ---------- Producten ----------
  async list(params: ProductListParams, opts: { silent?: boolean } = {}): Promise<Paged<Product>> {
    const { data } = await api.get<Paged<Product>>('/products', { params, ...opts });
    return data;
  },

  async create(body: CreateProductBody): Promise<Product> {
    const { data } = await api.post<Product>('/products', body);
    return data;
  },

  async get(id: number, opts: { silent?: boolean; noToast?: boolean } = {}): Promise<ProductWithLines> {
    const { data } = await api.get<ProductWithLines>(`/products/${id}`, opts);
    return data;
  },

  /** 409 VERSION_MISMATCH als `version` verouderd is; 400 CODE_LOCKED als product al in Unit4 staat. */
  async update(id: number, body: UpdateProductBody, opts: { noToast?: boolean } = {}): Promise<Product> {
    const { data } = await api.put<Product>(`/products/${id}`, body, opts);
    return data;
  },

  /** 409 PRODUCT_IN_USE (details.parents) als product als onderdeel gebruikt wordt; `force` overrulet dat. */
  async remove(id: number, force = false, opts: { noToast?: boolean } = {}): Promise<void> {
    await api.delete(`/products/${id}`, { params: force ? { force: 'true' } : undefined, ...opts });
  },

  async duplicate(id: number): Promise<Product> {
    const { data } = await api.post<Product>(`/products/${id}/duplicate`);
    return data;
  },

  /** Gearchiveerd product terugzetten. */
  async restore(id: number): Promise<Product> {
    const { data } = await api.post<Product>(`/products/${id}/restore`);
    return data;
  },

  /** Loskoppelen van Unit4 → verder als nieuw, nog niet gepusht product. */
  async detach(id: number): Promise<Product> {
    const { data } = await api.post<Product>(`/products/${id}/detach`);
    return data;
  },

  /** Plaatsing in de productenlijst (root) aan/uit; ouders (composite-regels) blijven ongewijzigd. */
  async setRoot(id: number, is_root: boolean, opts: { noToast?: boolean } = {}): Promise<Product> {
    const { data } = await api.post<Product>(`/products/${id}/root`, { is_root }, opts);
    return data;
  },

  async parents(id: number): Promise<Product[]> {
    const { data } = await api.get<{ items: Product[] }>(`/products/${id}/parents`, { silent: true });
    return data.items;
  },

  async tree(id: number, depth = 10): Promise<TreeNode> {
    const { data } = await api.get<TreeNode>(`/products/${id}/tree`, { params: { depth } });
    return data;
  },

  // ---------- Regels (bulk) ----------
  /** Primaire save van de regel-editor: id null = nieuw, ontbrekende id's worden verwijderd. */
  async replaceLines(
    id: number,
    version: number,
    lines: LineInput[],
    opts: { noToast?: boolean } = {},
  ): Promise<ProductWithLines> {
    const { data } = await api.put<ProductWithLines>(`/products/${id}/lines`, { version, lines }, opts);
    return data;
  },

  async addLine(id: number, body: Omit<LineInput, 'sort_order'>): Promise<ProductLine> {
    const { data } = await api.post<ProductLine>(`/products/${id}/lines`, body);
    return data;
  },

  // ---------- Regels (individueel) ----------
  async getLine(lineId: number): Promise<ProductLine> {
    const { data } = await api.get<ProductLine>(`/lines/${lineId}`);
    return data;
  },

  async patchLine(lineId: number, patch: Partial<LineInput>): Promise<ProductLine> {
    const { data } = await api.patch<ProductLine>(`/lines/${lineId}`, patch);
    return data;
  },

  async deleteLine(lineId: number): Promise<void> {
    await api.delete(`/lines/${lineId}`);
  },

  async moveLine(
    lineId: number,
    body: { target_product_id?: number; sort_order: number },
  ): Promise<ProductLine> {
    const { data } = await api.post<ProductLine>(`/lines/${lineId}/move`, body);
    return data;
  },

  /** Koppelt regel aan Unit4-product (haalt omschrijving/eenheid op). 404 als code niet bestaat in Unit4. */
  async linkLine(
    lineId: number,
    unit4_product_code: string,
    unit_code?: string | null,
  ): Promise<LinkLineResult> {
    const { data } = await api.post<LinkLineResult>(`/lines/${lineId}/link`, {
      unit4_product_code,
      unit_code: unit_code || undefined,
    });
    return data;
  },

  async makeComposite(
    lineId: number,
    body: { name: string } | { child_product_id: number },
  ): Promise<MakeCompositeResult> {
    const { data } = await api.post<MakeCompositeResult>(`/lines/${lineId}/make-composite`, body);
    return data;
  },

  // ---------- Instellingen (productcode) ----------
  async submitReview(id: number): Promise<SubmitReviewResult> {
    const { data } = await api.post<SubmitReviewResult>(`/products/${id}/submit-review`);
    return data;
  },

  async withdrawReview(id: number): Promise<WithdrawReviewResult> {
    const { data } = await api.post<WithdrawReviewResult>(`/products/${id}/withdraw-review`);
    return data;
  },

  async rejectReview(id: number, comment: string): Promise<Product> {
    const { data } = await api.post<Product>(`/products/${id}/reject-review`, { comment });
    return data;
  },
};
